"use client";

import { useRef, useState } from "react";
import { ImageOff, X } from "@app-foundation/icons/lucide";
import { portraitViews, type GalleryImage } from "../gallery";
import type { PlazaAvatar } from "../types";
import "./portrait-gallery.css";

type GalleryProps = Pick<PlazaAvatar, "title" | "imageUrl" | "leftImageUrl" | "rightImageUrl" | "lifePhotoUrls" | "stillPhotoUrls">;

function Photo({ image, title, priority = false }: { image: GalleryImage; title: string; priority?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (!image.url || failed) return <span className="portrait-missing"><ImageOff size={24} aria-hidden="true" /><span>{image.url ? "图片暂时无法加载" : `${image.label}待补充`}</span></span>;
  // Public derivatives are deliberately visible; UI restrictions are not file access control.
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={image.url} alt={`${title} · ${image.label}`} loading={priority ? "eager" : "lazy"} draggable={false} referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
}

export function PortraitGallery(avatar: GalleryProps) {
  const views = portraitViews(avatar);
  const usedUrls = new Set(views.map(view => view.url?.split("?")[0]).filter(Boolean));
  const portfolio = [...avatar.lifePhotoUrls, ...(avatar.stillPhotoUrls ?? [])]
    .filter(url => {
      const identity = url.split("?")[0];
      if (!identity || usedUrls.has(identity)) return false;
      usedUrls.add(identity);
      return true;
    })
    .map((url, i) => ({ url, label: `生活照 ${i + 1}` }));
  const available = [...views, ...portfolio].filter((view): view is GalleryImage & { url: string } => Boolean(view.url));
  const [active, setActive] = useState(0);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const open = (url: string, button: HTMLButtonElement) => {
    trigger.current = button;
    setActive(available.findIndex(view => view.url === url));
    dialog.current?.showModal();
  };
  const step = (offset: number) => setActive(index => (index + offset + available.length) % available.length);
  const image = available[active];
  const tile = (view: GalleryImage, index: number) => (
    <div className={`portrait-tile ${index === 0 ? "portrait-main" : ""}`} key={view.label}>
      {view.url ? <button type="button" aria-label={`放大查看${avatar.title}${view.label}`} onClick={event => open(view.url!, event.currentTarget)}>
        <Photo key={view.url} image={view} title={avatar.title} priority={index === 0} />
      </button> : <Photo image={view} title={avatar.title} />}
    </div>
  );
  return <section className="portrait-gallery" aria-label={`${avatar.title}形象照片`}>
    <div className="portrait-heading"><h2>形象照</h2></div>
    <div className="portrait-views">{views.map(tile)}</div>
    {portfolio.length > 0 && <section className="portrait-collection portrait-life" aria-label="生活照">
      <header><h3>生活照</h3></header>
      <div className="portrait-life-window">
        <div className="portrait-life-track">
          <div className="portrait-life-group">{portfolio.map((view, index) => tile(view, index + 3))}</div>
        </div>
      </div>
    </section>}

    <dialog ref={dialog} className="portrait-dialog" aria-label={`${avatar.title}照片查看器`} onClose={() => trigger.current?.focus({ preventScroll: true })} onClick={event => { if (event.target === event.currentTarget) dialog.current?.close(); }} onKeyDown={event => {
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") { event.preventDefault(); step(event.key === "ArrowRight" ? 1 : -1); }
    }}>
      <button type="button" className="portrait-close" aria-label="关闭照片查看器" onClick={() => dialog.current?.close()}><X size={24} /></button>
      {image && <div className="portrait-dialog-photo"><Photo key={image.url} image={image} title={avatar.title} priority /></div>}
    </dialog>
  </section>;
}
